import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { TokenStorageService } from './token-storage.service';

/**
 * Guardia de ruta funcional para el control de acceso basado en roles.
 *
 * Evalúa si el usuario autenticado posee los privilegios necesarios (ej. Admin o Editor)
 * para acceder a una vista. Extrae los roles permitidos desde la propiedad estática
 * `data.roles` configurada en la definición de la ruta. Si la ruta no especifica roles,
 * permite el acceso libremente. Si el usuario carece del rol requerido o no existe,
 * bloquea la navegación y redirige al login.
 *
 * @param route La instantánea de la ruta que se intenta activar, de la cual se extraen los datos (`data`).
 * @returns `true` si la validación es exitosa; de lo contrario, un `UrlTree` que redirige a `/login`.
 *
 * @example
 * // Uso típico en la definición de rutas (app.routes.ts) para gestionar noticias:
 * export const routes: Routes = [
 * {
 * path: 'gestor-noticias',
 * canActivate: [roleGuard],
 * data: { roles: ['admin', 'editor'] },
 * loadComponent: () => import('./noticias.component').then(m => m.NoticiasComponent)
 * }
 * ];
 */
export const roleGuard: CanActivateFn = (route) => {
  const tokenStorage = inject(TokenStorageService);
  const router = inject(Router);

  const user = tokenStorage.getUser();
  const expectedRoles = (route.data?.['roles'] as string[] | undefined) ?? [];

  if (expectedRoles.length === 0) {
    return true;
  }

  if (!user || !expectedRoles.includes(user.role)) {
    return router.createUrlTree(['/login']);
  }

  return true;
};
